import React, { useMemo } from 'react';
import Dropzone, { DropEvent, DropzoneOptions, FileRejection } from 'react-dropzone';
import { useTranslation } from 'react-i18next';

import { Box, SxProps, Typography } from '@mui/material';
import { FieldProps, Field as FormikField } from 'formik';

import useFileDrop from '../hooks/useFileDrop';
import DragTextDefault from './DragText/default';
import DragTextDragOver from './DragText/drag-over';
import UploadedFiles from './UploadedFiles';
import FILES_ACCEPTED_TYPES, { AcceptedTypesKeys } from '../constants/types';

import {
	FileUploadButton,
	FileUploadErrortext,
	FileUploadInsideWrap,
	FileUploadSection,
	FileUploadTextWrap,
	FileUploadWrap,
	UploadCloudIcon,
} from './style';

export interface FileUploadPropsType extends DropzoneOptions {
	acceptFiles: AcceptedTypesKeys | AcceptedTypesKeys[];
	formatsText: string;
	name: string;
	showBgImage?: boolean;
	sx?: SxProps;
	disabled?: boolean;
}

export function FileUpload({
	disabled = false,
	onDropAccepted,
	onDropRejected,
	acceptFiles,
	formatsText,
	name,
	multiple = false,
	maxSize,
	sx,
	...props
}: FileUploadPropsType) {
	const {
		files,
		handleDelete,
		isDragOver,
		onDragEnter,
		onDragLeave,
		onDrop,
		onReject,
		errorType,
	} = useFileDrop({
			multiple,
			name,
		});

	const { t } = useTranslation();

	const acceptFilesTypes = useMemo(() => {
		const keysArray = Array.isArray(acceptFiles) ? acceptFiles : [acceptFiles];

		return keysArray.reduce((acc, key) => {
			if (FILES_ACCEPTED_TYPES[key]) {
				return { ...acc, ...FILES_ACCEPTED_TYPES[key] };
			}

			return acc;
		}, {} as { [x: string]: string[] });
	}, [acceptFiles]);

	const handleDropRejected = (rejectedFiles: FileRejection[], event: DropEvent) => {
		onReject(rejectedFiles, event, maxSize);

		if (onDropRejected) {
			onDropRejected(rejectedFiles, event);
		}
	};

	const handleDrop = (acceptedFiles: File[], fileRejections: FileRejection[], event: DropEvent) => {
		onDrop(acceptedFiles);
		if (onDropAccepted) {
			onDropAccepted(acceptedFiles, event);
		}
	};

	return (
		<FileUploadSection sx={sx}>
			<FormikField name={name}>
				{({ meta }: FieldProps) => {
					const isError = meta.touched && !!meta.error;
					const renderUploadedLinks = !!files && files?.length > 0;

					if (renderUploadedLinks) {
						return (
							<UploadedFiles
								links={files}
								handleDelete={!disabled ? handleDelete : undefined}
							/>
						);
					}

					return (
						<>
							{/* @ts-ignore */}
							<FileUploadWrap isDragOver={isDragOver} isError={isError}>
								<Dropzone
									onDropRejected={handleDropRejected}
									onDrop={handleDrop}
									accept={acceptFilesTypes}
									onDragEnter={onDragEnter}
									onDragLeave={onDragLeave}
									multiple={multiple}
									maxSize={maxSize * 1024 * 1024} // in bytes
									{...props}
								>
									{({ getRootProps, getInputProps }) => (
										<FileUploadInsideWrap {...getRootProps()}>
											<input {...getInputProps()} />
											<UploadCloudIcon />
											<FileUploadTextWrap>
												{isDragOver ? <DragTextDefault /> : <DragTextDragOver />}
												<Typography variant="caption1" sx={{ opacity: '0.5' }}>
													{formatsText}
												</Typography>
											</FileUploadTextWrap>
											<Box textAlign="start" flex={1}>
												<FileUploadButton variant="outlined" color="primary">
													{t('report.achievements.addFileModal.selectFile')}
												</FileUploadButton>
											</Box>
										</FileUploadInsideWrap>
									)}
								</Dropzone>
							</FileUploadWrap>
							{isError && (
								<FileUploadErrortext variant="caption1">{meta.error}</FileUploadErrortext>
							)}
						</>
					);
				}}
			</FormikField>
			{errorType && <FileUploadErrortext variant="caption1">{errorType}</FileUploadErrortext>}
		</FileUploadSection>
	);
}
